<template>
  <layout-report-step :isDone="isDone" :title="title" :description="description">
    <drop-area-multi
      accept=".pdf"
      :max-files="maxFiles"
      :text="resolvedDropText"
      :dragover-text="resolvedDragoverText"
      :files="allFiles"
      @dropped="handleDrop"
    >
      <template #chips>
        <div v-if="fileEntries.length" style="display: flex; flex-wrap: wrap; justify-content: center; margin-top: 8px">
          <v-chip
            v-for="(entry, i) in fileEntries"
            :key="i"
            small
            :color="chipColor(entry)"
            style="margin: 2px; max-width: 220px"
          >
            <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; min-width: 0">
              {{ entry.file.name }}
            </span>
            <v-progress-circular
              v-if="entry.state === FileEntryState.UPLOADING"
              size="14"
              width="2"
              indeterminate
              class="ml-1"
              style="flex-shrink: 0"
            />
            <v-icon
              v-if="entry.state === FileEntryState.ERROR"
              small
              class="ml-1"
              style="cursor: pointer; flex-shrink: 0"
              @click.stop="handleRetry(entry)"
              >mdi-refresh</v-icon
            >
            <v-icon
              v-if="entry.state !== FileEntryState.UPLOADING"
              small
              class="ml-1"
              style="cursor: pointer; flex-shrink: 0"
              @click.stop="handleClose(entry)"
            >
              mdi-close
            </v-icon>
          </v-chip>
        </div>
      </template>
    </drop-area-multi>
  </layout-report-step>
</template>

<script lang="ts">
import DropAreaMulti from "@/components/files/DropAreaMulti.vue";
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import documentService from "@/services/mrfiktiv/services/documentService";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ReportModule } from "@/store/modules/report.store";
import { Component, Prop } from "vue-property-decorator";
import ReportStepMixin from "@/mixins/ReportStepMixin.vue";
import { VuetifyColorEnum } from "@/lib/enum/vuetify-color.enum";

enum FileEntryState {
  IDLE = "idle",
  UPLOADING = "uploading",
  DONE = "done",
  ERROR = "error"
}

interface FileEntry {
  file: File;
  state: FileEntryState;
  docId: string | undefined;
  objectUrl: string | undefined;
}

const MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024;

@Component({
  components: {
    LayoutReportStep,
    DropAreaMulti
  }
})
export default class ReportDocumentUpload extends ReportStepMixin {
  readonly FileEntryState = FileEntryState;

  screen = ReportScreenEnum.documentupload;

  @Prop({ type: Number, default: 0 })
  minFiles!: number;

  @Prop({ default: 10 })
  maxFiles!: number;

  @Prop({ type: String, default: "report.documentUpload.title" })
  title!: string;

  @Prop({ type: String, default: "report.documentUpload.description" })
  description!: string;

  @Prop({ type: String, default: "" })
  dropText!: string;

  @Prop({ type: String, default: "" })
  dragoverText!: string;

  fileEntries: FileEntry[] = [];

  get isDone(): boolean {
    if (this.fileEntries.some((e) => e.state === FileEntryState.UPLOADING)) return false;
    const doneCount = this.fileEntries.filter((e) => e.state === FileEntryState.DONE).length;
    return doneCount >= this.minFiles;
  }

  get allFiles(): File[] {
    return this.fileEntries.map((e) => e.file);
  }

  get resolvedDropText(): string {
    return this.dropText || (this.$t("report.documentUpload.dropText") as string);
  }

  get resolvedDragoverText(): string {
    return this.dragoverText || (this.$t("report.documentUpload.dragoverText") as string);
  }

  chipColor(entry: FileEntry): string {
    if (entry.state === FileEntryState.DONE) return VuetifyColorEnum.SUCCESS;
    if (entry.state === FileEntryState.ERROR) return VuetifyColorEnum.ERROR;
    return "";
  }

  async handleDrop(files: File[]) {
    const newFiles = files.filter((f) => !this.fileEntries.some((e) => e.file === f));
    for (const file of newFiles) {
      this.fileEntries.push({ file, state: FileEntryState.IDLE, docId: undefined, objectUrl: undefined });
    }
    await this.uploadIdle();
  }

  private async uploadIdle() {
    const idleEntries = this.fileEntries.filter((e) => e.state === FileEntryState.IDLE);
    await Promise.all(idleEntries.map((entry) => this.uploadEntry(entry)));
  }

  private async uploadEntry(entry: FileEntry) {
    entry.state = FileEntryState.UPLOADING;
    if (entry.file.size > MAX_FILE_SIZE_BYTES) {
      this.$toast.error(this.$t("report.documentUpload.fileTooLarge") as string);
      entry.state = FileEntryState.ERROR;
      return;
    }
    try {
      const doc = await documentService.createPublic(ReportModule.partner.id, entry.file);
      if (!this.fileEntries.includes(entry)) return;
      entry.objectUrl = URL.createObjectURL(entry.file);
      entry.docId = doc.id;
      entry.state = FileEntryState.DONE;
      ReportModule.addDocumentId(doc.id);
    } catch (e) {
      if (!this.fileEntries.includes(entry)) return;
      this.$log.error(e);
      this.$toast.error(this.$t("report.documentUpload.uploadError") as string);
      entry.state = FileEntryState.ERROR;
    }
  }

  async handleRetry(entry: FileEntry) {
    entry.state = FileEntryState.IDLE;
    await this.uploadEntry(entry);
  }

  handleClose(entry: FileEntry) {
    if (entry.state === FileEntryState.DONE) {
      if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
      if (entry.docId) ReportModule.removeDocumentId(entry.docId);
    }
    this.fileEntries = this.fileEntries.filter((e) => e !== entry);
  }

  handleDelete(entry: FileEntry) {
    if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
    if (entry.docId) ReportModule.removeDocumentId(entry.docId);
    this.fileEntries = this.fileEntries.filter((e) => e !== entry);
  }

  beforeDestroy() {
    for (const entry of this.fileEntries) {
      if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
    }
  }
}
</script>
